import React, { useCallback, useEffect, useState } from 'react'
import {
  AudioLines,
  ListChecks,
  LoaderCircle,
  Play,
  RefreshCw,
  Target,
  TrendingUp,
} from 'lucide-react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { api, getApiErrorMessage } from '../../lib/api'
import CounselPracticeDerivedAnalysis from './CounselPracticeDerivedAnalysis'

function unwrap(response) {
  return response?.data?.data ?? response?.data ?? null
}

function number(value) {
  return new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 1 }).format(Number(value) || 0)
}

function shortDate(value) {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '' : `${date.getMonth() + 1}.${date.getDate()}`
}

export default function CounselPracticeGrowthReport({ onPracticeContent }) {
  const [days, setDays] = useState(90)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const response = await api.get('/counsel-practice/insights/me', {
        params: { days },
        __silent: true,
      })
      setData(unwrap(response))
    } catch (e) {
      setError(getApiErrorMessage(e, '성장 분석을 불러오지 못했습니다.'))
    } finally {
      setLoading(false)
    }
  }, [days])

  useEffect(() => {
    load()
  }, [load])

  if (loading) {
    return (
      <div className="flex min-h-[28rem] items-center justify-center text-slate-500">
        <LoaderCircle className="h-6 w-6 animate-spin" aria-label="성장 분석 불러오는 중" />
      </div>
    )
  }

  const learningProfile = data?.learningProfile || {}
  const linkedEffect = data?.linkedPracticeEffect || {}
  const trend = Array.isArray(data?.trend) ? data.trend : []
  const sections = Array.isArray(data?.sections) ? data.sections : []
  const recommendations = Array.isArray(data?.recommendations) ? data.recommendations : []
  const learningPlan = data?.learningPlan || {}
  const planSteps = Array.isArray(learningPlan.steps) ? learningPlan.steps : []
  const focusAreas = Array.isArray(data?.focusAreas) ? data.focusAreas : []
  const speech = data?.speechHabits || {}
  const speechCoaching = Array.isArray(data?.speechCoaching) ? data.speechCoaching : []
  const weakest = data?.weakestSection || {}
  const weeklyTarget = Math.max(1, Number(learningPlan.weeklyTarget) || 3)
  const completedThisWeek = Math.min(weeklyTarget, Number(learningPlan.completedThisWeek) || 0)
  const planProgress = Math.round(completedThisWeek * 100 / weeklyTarget)

  return (
    <section className="mt-5" aria-labelledby="counsel-growth-title" data-testid="counsel-growth-report">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-4">
        <div>
          <h2 id="counsel-growth-title" className="text-lg font-semibold text-slate-950">나의 성장</h2>
          <p className="mt-1 text-sm text-slate-600">완료한 연습 기록에서 점수 흐름과 다음 연습 방향을 확인해 보세요.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={days} onChange={(event) => setDays(Number(event.target.value))} className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-700" aria-label="성장 분석 기간">
            <option value={30}>최근 30일</option>
            <option value={90}>최근 90일</option>
            <option value={180}>최근 180일</option>
            <option value={365}>최근 1년</option>
          </select>
          <button type="button" onClick={load} className="flex h-9 w-9 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-600 hover:bg-slate-50" title="새로고침" aria-label="성장 분석 새로고침"><RefreshCw className="h-4 w-4" aria-hidden="true" /></button>
        </div>
      </div>

      {error && <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" role="alert">{error}</div>}

      <div className="mt-5 grid overflow-hidden border border-slate-200 bg-white sm:grid-cols-3">
        <div className="px-5 py-4"><div className="flex items-center gap-2 text-xs font-medium text-slate-500"><TrendingUp className="h-4 w-4 text-sky-700" aria-hidden="true" />완료한 연습</div><div className="mt-2 text-2xl font-semibold text-slate-950">{number(data?.completedSessions)}회</div></div>
        <div className="border-t border-slate-200 px-5 py-4 sm:border-l sm:border-t-0"><div className="text-xs font-medium text-slate-500">평균 점수</div><div className="mt-2 text-2xl font-semibold text-slate-950">{number(data?.averageScore)}점</div></div>
        <div className="border-t border-slate-200 px-5 py-4 sm:border-l sm:border-t-0"><div className="flex items-center gap-2 text-xs font-medium text-slate-500"><Target className="h-4 w-4 text-amber-700" aria-hidden="true" />먼저 연습할 항목</div><div className="mt-2 text-base font-semibold text-slate-950">{weakest.label || '연습 결과가 더 필요합니다'}</div>{weakest.label && <div className="mt-1 text-xs text-slate-500">평균 {number(weakest.averageScore)}점</div>}</div>
      </div>

      {learningProfile.personaLabel && (
        <section className="mt-5 border-y border-indigo-200 bg-indigo-50 px-4 py-4" aria-labelledby="learning-profile-title" data-testid="counsel-learning-profile">
          <h3 id="learning-profile-title" className="text-sm font-semibold text-indigo-950">이번 연습 설정</h3>
          <dl className="mt-3 grid gap-3 sm:grid-cols-3">
            <div><dt className="text-xs text-indigo-700">추천 고객</dt><dd className="mt-1 text-sm font-semibold text-slate-900">{learningProfile.personaLabel}</dd></div>
            <div><dt className="text-xs text-indigo-700">추천 난이도</dt><dd className="mt-1 text-sm font-semibold text-slate-900">{learningProfile.difficultyLabel}</dd></div>
            <div><dt className="text-xs text-indigo-700">최근 흐름</dt><dd className="mt-1 text-sm font-semibold text-slate-900">{learningProfile.trendLabel} · 평균 {number(learningProfile.recentAverage)}점</dd></div>
          </dl>
        </section>
      )}

      {Number(linkedEffect.analysisCount) > 0 && (
        <section className="mt-5 border-y border-teal-200 bg-teal-50 px-4 py-4" aria-labelledby="linked-effect-title" data-testid="counsel-linked-practice-effect">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 id="linked-effect-title" className="text-sm font-semibold text-teal-950">실제 상담 연계 효과</h3>
            <span className="text-xs font-semibold text-teal-800">{linkedEffect.statusLabel}</span>
          </div>
          <dl className="mt-3 grid gap-3 sm:grid-cols-4">
            <div><dt className="text-xs text-teal-700">분석</dt><dd className="mt-1 text-sm font-semibold text-slate-900">{number(linkedEffect.analysisCount)}건</dd></div>
            <div><dt className="text-xs text-teal-700">연습 시작</dt><dd className="mt-1 text-sm font-semibold text-slate-900">{number(linkedEffect.startedCount)}건</dd></div>
            <div><dt className="text-xs text-teal-700">연습 완료</dt><dd className="mt-1 text-sm font-semibold text-slate-900">{number(linkedEffect.completedCount)}건</dd></div>
            <div><dt className="text-xs text-teal-700">평균 변화</dt><dd className="mt-1 text-sm font-semibold text-slate-900">{Number(linkedEffect.averageImprovement) > 0 ? '+' : ''}{number(linkedEffect.averageImprovement)}점</dd></div>
          </dl>
        </section>
      )}

      {trend.length === 0 ? (
        <div className="mt-6 border-y border-slate-200 py-12 text-center text-sm text-slate-500">연습을 완료하면 점수 흐름이 표시됩니다.</div>
      ) : (
        <div className="mt-6 grid gap-6 xl:grid-cols-2">
          <section className="min-w-0 border-t border-slate-200 pt-5" aria-labelledby="score-trend-title">
            <h3 id="score-trend-title" className="text-base font-semibold text-slate-950">점수 흐름</h3>
            <div className="mt-4 h-64 w-full" aria-label="연습 점수 흐름 그래프">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trend} margin={{ top: 8, right: 12, left: -20, bottom: 0 }}>
                  <CartesianGrid stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="completedAt" tickFormatter={shortDate} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <Tooltip labelFormatter={shortDate} formatter={(value) => [`${number(value)}점`, '점수']} />
                  <Line dataKey="score" type="monotone" stroke="#0284c7" strokeWidth={2.5} dot={{ r: 3, fill: '#0284c7' }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section className="min-w-0 border-t border-slate-200 pt-5" aria-labelledby="section-average-title">
            <h3 id="section-average-title" className="text-base font-semibold text-slate-950">항목별 평균</h3>
            <div className="mt-4 h-64 w-full" aria-label="항목별 평균 점수 그래프">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={sections} layout="vertical" margin={{ top: 4, right: 16, left: 10, bottom: 0 }}>
                  <CartesianGrid stroke="#e2e8f0" horizontal={false} />
                  <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="label" width={58} tick={{ fontSize: 12, fill: '#475569' }} axisLine={false} tickLine={false} />
                  <Tooltip formatter={(value) => [`${number(value)}점`, '평균']} />
                  <Bar dataKey="averageScore" fill="#0f766e" radius={[0, 3, 3, 0]} maxBarSize={22} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>
        </div>
      )}

      <section className="mt-6 border-t border-slate-200 pt-5" aria-labelledby="weekly-plan-title">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2"><ListChecks className="h-5 w-5 text-indigo-700" aria-hidden="true" /><h3 id="weekly-plan-title" className="text-base font-semibold text-slate-950">이번 주 연습 계획</h3></div>
            <p className="mt-2 text-sm text-slate-600">{learningPlan.focusLabel ? `${learningPlan.focusLabel}에 집중해 ${weeklyTarget}번 연습합니다.` : '완료한 연습을 보고 다음 순서를 정합니다.'}</p>
            {focusAreas.length > 0 && <p className="mt-1 text-xs text-slate-500">집중할 부분: {focusAreas.map((item) => item.label).join(' · ')}</p>}
          </div>
          <div className="text-right"><div className="text-sm font-semibold text-slate-900">{completedThisWeek} / {weeklyTarget}회</div><div className="mt-1 text-xs text-slate-500">최근 7일</div></div>
        </div>
        <div className="mt-4 h-2 overflow-hidden rounded bg-slate-100" role="progressbar" aria-label="이번 주 연습 달성률" aria-valuemin={0} aria-valuemax={100} aria-valuenow={planProgress}><div className="h-full bg-indigo-600" style={{ width: `${planProgress}%` }} /></div>
        <div className="mt-4 divide-y divide-slate-200 border-y border-slate-200">
          {planSteps.map((item) => (
            <div key={`${item.contentType}-${item.contentCode}`} className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <div className="text-xs font-semibold text-indigo-700">{item.stepLabel} · {item.contentTypeLabel}</div>
                <div className="mt-1 font-semibold text-slate-900">{item.title}</div>
                <p className="mt-1 text-sm text-slate-600">목표: {item.target}</p>
              </div>
              <button type="button" onClick={() => onPracticeContent?.(item.contentType, item.contentCode, item.recommendedPersona, item.recommendedDifficulty)} className="inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"><Play className="h-4 w-4" aria-hidden="true" />연습 시작</button>
            </div>
          ))}
          {planSteps.length === 0 && <p className="py-8 text-center text-sm text-slate-500">연습을 완료하면 이번 주 계획이 만들어집니다.</p>}
        </div>
      </section>

      <section className="mt-6 border-t border-slate-200 pt-5" aria-labelledby="growth-speech-title">
        <div className="flex items-center gap-2"><AudioLines className="h-5 w-5 text-teal-700" aria-hidden="true" /><h3 id="growth-speech-title" className="text-base font-semibold text-slate-950">말하기 습관 누적</h3></div>
        {Number(speech.measuredSessions) > 0 ? (
          <dl className="mt-4 grid border-y border-slate-200 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
            {[
              ['분석한 음성 연습', `${number(speech.measuredSessions)}회`],
              ['평균 말하기 속도', `분당 ${number(speech.averagePacePerMinute)}어절`],
              ['답변당 평균 길이', `${number(speech.averageWordsPerAnswer)}어절`],
              ['분당 군더더기 표현', `${number(speech.fillerPerMinute)}회`],
              ['속도 변화', `${number(speech.paceVariationPercent)}%`],
              ['긴 답변', `${number(speech.longAnswerCount)}회`],
              ['인식이 불분명한 답변', `${number(speech.lowConfidenceCount)}회`],
              ['질문으로 마무리', `${number(speech.questionEndingCount)}회`],
              ['같은 말로 시작', `${number(speech.repeatedStartCount)}회`],
              ['긴 멈춤', `${number(speech.pauseCount)}회`],
              ['평균 침묵 비율', `${number(speech.averageSilencePercent)}%`],
              ['평균 음성 인식률', `${number(speech.averageConfidence)}%`],
            ].map(([label, value]) => <div key={label} className="px-4 py-4"><dt className="text-xs font-medium text-slate-500">{label}</dt><dd className="mt-1 text-sm font-semibold text-slate-900">{value}</dd></div>)}
          </dl>
        ) : <p className="mt-4 text-sm text-slate-500">음성 연습을 완료하면 말하기 습관이 쌓입니다.</p>}
        {speechCoaching.length > 0 && (
          <div className="mt-3 divide-y divide-slate-200 border-y border-slate-200" data-testid="counsel-speech-coaching">
            {speechCoaching.map((tip, index) => <p key={`${index}-${tip}`} className="py-3 text-sm leading-6 text-slate-700"><span className="mr-2 font-semibold text-teal-700">{index + 1}</span>{tip}</p>)}
          </div>
        )}

      </section>

      <section className="mt-6 border-t border-slate-200 pt-5" aria-labelledby="recommended-mission-title">
        <h3 id="recommended-mission-title" className="text-base font-semibold text-slate-950">추천 연습</h3>
        {data?.nextAction && <p className="mt-2 text-sm leading-6 text-slate-600">{data.nextAction}</p>}
        <div className="mt-4 divide-y divide-slate-200 border-y border-slate-200">
          {recommendations.map((item) => (
            <div key={item.contentCode} className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <div className="font-semibold text-slate-900">{item.title}</div>
                <div className="mt-1 text-xs text-slate-500">{item.insuranceTypeLabel} · {item.missionTypeLabel}</div>
                <p className="mt-2 text-sm leading-6 text-slate-600">{item.why}</p>
              </div>
              <button type="button" onClick={() => onPracticeContent?.(item.contentType, item.contentCode, item.recommendedPersona, item.recommendedDifficulty)} className="inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-md bg-sky-600 px-3 text-sm font-semibold text-white hover:bg-sky-700"><Play className="h-4 w-4" aria-hidden="true" />{item.contentType === 'FULL_SCENARIO' ? '이 상담 연습' : '이 미션 연습'}</button>
            </div>
          ))}
          {recommendations.length === 0 && <p className="py-8 text-center text-sm text-slate-500">현재 추천할 미션이 없습니다.</p>}
        </div>
      </section>

      <CounselPracticeDerivedAnalysis onPracticeContent={onPracticeContent} />
    </section>
  )
}
